import "./style.css";
import "./editor-styles.css";

import ExampleTheme from "./plugins/Theme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import { $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, LexicalEditor } from "lexical";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { InitialConfigType } from "@lexical/react/LexicalComposer";

// Import our inline styled HTML export function
import { generateExportHtmlWithInlineStyles } from "./inlineStyledHtmlExport";

const placeholder = "Enter some rich text...";

// Editor theme for styling during editing
const editorTheme = {
	...ExampleTheme,
	list: {
		nested: {
			listitem: "editor-nested-listitem"
		},
		ol: "editor-list-ol",
		ul: "editor-list-ul",
		listitem: "editor-listitem"
	},
	text: {
		bold: "editor-text-bold",
		italic: "editor-text-italic",
		underline: "editor-text-underline",
		strikethrough: "editor-text-strikethrough",
		code: "editor-text-code"
	}
};

export default function RichTextEditorWithInlineStyles({
	defaultHtml,
	setHtml,
}: {
	defaultHtml?: string,
	setHtml: React.Dispatch<React.SetStateAction<string>>;
}) {
	const editorConfig: InitialConfigType = {
		namespace: "RichTextEditor",
		theme: editorTheme,
		onError(error: any) {
			console.error("Editor error:", error);
			if (error.fatal) {
				throw error;
			}
		},
		editorState(editor: LexicalEditor) {
			try {
				if (defaultHtml) {
					const parser = new DOMParser();
					const dom = parser.parseFromString(defaultHtml, "text/html");
					const nodes = $generateNodesFromDOM(editor, dom);
					$getRoot().select().insertNodes(nodes);
				}
			} catch (error) {
				console.error("Error initializing editor with HTML:", error);
			}
		},
		nodes: [
			HeadingNode,
			ListNode,
			ListItemNode,
			QuoteNode,
			CodeNode,
			CodeHighlightNode,
			TableNode,
			TableCellNode,
			TableRowNode,
			AutoLinkNode,
			LinkNode
		]
	};

	return (
		<LexicalComposer initialConfig={editorConfig}>
			<div className="editor-container">
				<ToolbarPlugin />
				<div className="editor-inner">
					<RichTextPlugin
						contentEditable={
							<ContentEditable 
								className="editor-input"
								aria-placeholder={placeholder}
								placeholder={
									<div className="editor-placeholder">{placeholder}</div>
								}
							/>
						}
						ErrorBoundary={LexicalErrorBoundary}
					/>
					<HistoryPlugin />
					<AutoFocusPlugin />
					<ListPlugin />
					<LinkPlugin />
					<ListMaxIndentLevelPlugin maxDepth={7} />
					<OnChangePlugin
						onChange={(editorState, editor) => {
							// Use our inline styled HTML export function
							const inlineStyledHtml = generateExportHtmlWithInlineStyles(editor);
							
							// Update the parent component with the styled HTML
							setHtml(inlineStyledHtml);
						}}
						ignoreHistoryMergeTagChange
						ignoreSelectionChange
					/>
				</div>
			</div>
		</LexicalComposer>
	);
}